import { IScope } from 'angular'
import ngRedux from 'ng-redux'
import { TabTypes, CurrencyDetails } from '@vend/business-components'
import {
  PROMO_CODE_STATUES,
  PROMO_CODE_STATUES_ERROR_MSGS,
} from '../../promotions/promotions.constant'
import { $rootScope, $q } from '../../../ng-compat/imports'
import { getPromotionById } from '../../promotions/promotions-resource/promotions-resource'
import { SaleManager } from '../../../managers/sale-manager'
import { RegisterManager } from '../../../managers/register-manager'
import { RetailerSettingsManager } from '../../../managers/retailer-settings-manager'
import { RootState } from '../../../state/store'
import {
  selectSalePromoCode,
  selectSalePromoCodePromotionId,
  selectSalePromoCodeStatus,
  setPromoCode,
  setPromoCodeStatus,
} from '../../../state/slices/currentSale/currentSaleSlice'

/**
 * Controller for the sale promo code popover.
 *
 * @class SalePromoCodePopoverController
 */
export class SalePromoCodePopoverController {
  public promoCode = ''
  private promoCodeStatus: string | null = null

  constructor(
    public close: $TSFixMe,
    public saleManager: SaleManager,
    public registerManager: RegisterManager,
    private readonly promotionModalService: $TSFixMe,
    private readonly retailerSettingsManager: RetailerSettingsManager,
    private readonly $ngRedux: ngRedux.INgRedux,
    private readonly $scope: IScope
  ) {
    'ngInject'

    const disconnectRedux = this.$ngRedux.connect((state: RootState) => ({
      promoCode: selectSalePromoCode(state),
      promoCodeStatus: selectSalePromoCodeStatus(state),
    }))(this)

    this.$scope.$on('$destroy', disconnectRedux)

    // Empty promoCodeStatus everytime open the promo code popover
    this.$ngRedux.dispatch(setPromoCodeStatus(null))
  }

  /**
   * Handler for adding promo code when click 'Add' button
   *
   * @method addPromoCode
   */
  public addPromoCode(): void {
    this.saleManager.addOrRemovePromoCode(this.promoCode).then(status => {
      if (status === PROMO_CODE_STATUES.APPLIED) {
        const promoCodeAdded = Boolean(this.promoCode)
        this.close(promoCodeAdded)

        return
      }

      this.$ngRedux.dispatch(setPromoCode(''))
      $rootScope.$emit('invalidPromoCode')
    })
  }

  /**
   * Check if current input promo code is correct
   *
   * @method isPromoCodeNotCorrect
   *
   * @return {Boolean} True if the promo code is not correct, false otherwise
   */
  public isPromoCodeNotCorrect(): boolean {
    return Boolean(
      this.promoCodeStatus &&
        this.promoCodeStatus !== PROMO_CODE_STATUES.APPLIED
    )
  }

  /**
   * Reset the promoCodeStatus when start to re-typing promo code after getting an error validation message
   *
   * @method removeErrorMsg
   */
  public removeErrorMsg(): void {
    if (this.isPromoCodeNotCorrect()) {
      this.$ngRedux.dispatch(setPromoCodeStatus(null))
    }
  }

  /**
   * Get the validation error message based on status param
   *
   * @method getValidationErrorMsg
   *
   * @return {String} A text message to show why current promo code can't be applied to the sale
   */
  public getValidationErrorMsg(): string {
    const status = this.promoCodeStatus!
    return (
      PROMO_CODE_STATUES_ERROR_MSGS[status] ||
      PROMO_CODE_STATUES_ERROR_MSGS.default
    )
  }

  /**
   * Tells us if the status is one of the condition-not-reached status'
   *
   * @method isStatusInsufficientBuyOrSpend
   *
   * @return {Boolean} Whether the promo code status is insufficient_buy or insufficient_spend or not
   */
  public isStatusInsufficientBuyOrSpend(): boolean {
    return (
      this.promoCodeStatus === PROMO_CODE_STATUES.INSUFFICIENT_BUY ||
      this.promoCodeStatus === PROMO_CODE_STATUES.INSUFFICIENT_SPEND
    )
  }

  /**
   * Tells us if the status is an action-not-reached status'
   *
   * @method isStatusInsufficientGet
   *
   * @return {Boolean} Whether the promo code status is insufficient_get
   */
  public isStatusInsufficientGet(): boolean {
    return this.promoCodeStatus === PROMO_CODE_STATUES.INSUFFICIENT_GET
  }

  /**
   * Open up the promotion modal
   *
   * @method openPromotionModal
   */
  public openPromotionModal() {
    const promoCodePromotionId = selectSalePromoCodePromotionId(
      this.$ngRedux.getState()
    )

    if (!promoCodePromotionId) {
      return
    }

    return $q.resolve(
      getPromotionById(promoCodePromotionId).then(promotion => {
        if (!promotion) {
          return
        }
        const retailer = this.retailerSettingsManager.get()
        const currencyDetails: CurrencyDetails = {
          culture: retailer.culture,
          code: retailer.currency.code,
          symbol: retailer.currency.symbol,
        }
        return this.promotionModalService.openPromotion({
          promotion,
          lineItemIds: this.saleManager.getLineItemProductIds(),
          isTaxExclusive: !this.registerManager.isTaxInclusive(),
          defaultTab: TabTypes.PromoCode,
          currencyDetails,
        })
      })
    )
  }

  /**
   * Determine if there is a link to the promotion modal in the error message
   *
   * @method showPromotionModalLink
   *
   * @return {boolean} True if we want to have a link in the error message to the promotion modal otherwise false
   */
  public showPromotionModalLink(): boolean {
    switch (this.promoCodeStatus) {
      case PROMO_CODE_STATUES.EXPIRED:
      case PROMO_CODE_STATUES.INCORRECT_OUTLET:
      case PROMO_CODE_STATUES.UPCOMING:
      case PROMO_CODE_STATUES.INSUFFICIENT_BUY:
      case PROMO_CODE_STATUES.INSUFFICIENT_SPEND:
      case PROMO_CODE_STATUES.INSUFFICIENT_GET:
        return true
      default:
        return false
    }
  }
}
